/**
 * PLANES DE LIMPIEZA DE UN CENTRO — PLAN-UX-01-B
 *
 * PLAN-UX-01-A permite publicar con fecha de corte FUTURA. Por eso esta
 * página NUNCA usa `estado === 'VIGENTE'` como sinónimo de "el plan que
 * rige hoy": son cosas distintas —el propio backend las distingue (0037,
 * `ex_planes_vigencia_operativa_sin_solape`) — y confundirlas mostraría
 * como "actual" un plan que todavía no ha entrado en vigor.
 *
 *   v3  estado SUSTITUIDO, vigente_hasta 14/09   ← el que rige HOY
 *   v4  estado VIGENTE,    vigente_desde 15/09   ← entra en vigor mañana
 *
 * `clasificarFamilia()` usa la MISMA regla temporal que el motor
 * (`fn_plan_version_aplicable`/`cargarVersionesEnHorizonte`: rango de
 * fechas, nunca el nombre del estado) para decidir cuál es cuál.
 *
 * Un centro puede tener más de un plan lógico (más de un `codigo`); cada
 * familia se clasifica y se muestra por separado.
 */
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { api, type Plan, type PlanGlobal, type LineaPlan, FalloApi } from '../api.ts';
import { Shell } from '../componentes/shell.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { CampoTexto, Error as ErrorCaja, EstadoPlan, hoyLocal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { useCentro } from './centro.tsx';
import { useSesion } from '../contexto.tsx';
import { DescargarPlantilla } from './importacion.tsx';

// ------------------------------------------------------------- clasificación

/** Hoy LOCAL: se compara con la vigencia del plan, que es una fecha local (UAT 9.6-B1-B). */
function hoyIso(): string {
  return hoyLocal();
}

export interface FamiliaClasificada {
  readonly codigo: string;
  /** Rige HOY: el motor generaría OT con este contenido si tocara generar ahora mismo. */
  readonly actual: Plan | null;
  /** VIGENTE, pero su `vigenteDesde` todavía no ha llegado. */
  readonly proxima: Plan | null;
  /** Como mucho una por familia (0037: `ux_planes_un_borrador_por_familia`). */
  readonly borrador: Plan | null;
  /** El resto: versiones ya sustituidas y superadas por `actual`. */
  readonly historico: readonly Plan[];
}

/**
 * Idéntica a la pregunta que resuelve `fn_plan_version_aplicable`: una
 * versión "rige hoy" si su rango [vigente_desde, vigente_hasta] (el
 * segundo inclusive, NULL = abierto) contiene la fecha de hoy. Ninguna
 * fila puede solapar con otra (0037 lo impone en la base), así que como
 * mucho una fila cumple esto.
 */
export function clasificarFamilia(codigo: string, planes: readonly Plan[]): FamiliaClasificada {
  const hoy = hoyIso();
  let actual: Plan | null = null;
  let proxima: Plan | null = null;
  let borrador: Plan | null = null;

  for (const p of planes) {
    if (p.estado === 'BORRADOR') {
      borrador = p;
      continue;
    }
    if (p.vigenteDesde === null) continue; // defensivo: no debería darse fuera de BORRADOR
    const rige = p.vigenteDesde <= hoy && (p.vigenteHasta === null || p.vigenteHasta >= hoy);
    if (rige) {
      actual = p;
    } else if (p.estado === 'VIGENTE' && p.vigenteDesde > hoy) {
      proxima = p;
    }
  }

  const historico = planes.filter((p) => p.id !== actual?.id && p.id !== proxima?.id && p.id !== borrador?.id);
  return { codigo, actual, proxima, borrador, historico };
}

/** Una familia (mismo código en el MISMO centro) con, si se conoce, de qué centro y cliente es. */
interface FamiliaConCentro extends FamiliaClasificada {
  readonly clave: string;
  readonly centro: { readonly idCentro: string; readonly codigo: string; readonly descripcion: string; readonly idCliente: string; readonly cliente: string } | null;
  readonly descripcion: string;
}

function agruparPorFamilia(planes: readonly (Plan | PlanGlobal)[]): FamiliaConCentro[] {
  const porClave = new Map<string, (Plan | PlanGlobal)[]>();
  for (const p of planes) {
    const clave = `${'idCentro' in p ? p.idCentro : ''}|${p.codigo}`;
    const lista = porClave.get(clave) ?? [];
    lista.push(p);
    porClave.set(clave, lista);
  }
  return [...porClave.entries()]
    .map(([clave, lista]) => {
      const p0 = lista[0]!;
      const centro = 'idCentro' in p0
        ? { idCentro: p0.idCentro, codigo: p0.centroCodigo, descripcion: p0.centroDescripcion, idCliente: p0.idCliente, cliente: p0.clienteRazonSocial }
        : null;
      const familia = clasificarFamilia(p0.codigo, lista);
      const referencia = familia.actual ?? familia.proxima ?? familia.borrador ?? lista[0]!;
      return { ...familia, clave, centro, descripcion: referencia.descripcion };
    })
    .sort((a, b) => (a.centro?.cliente ?? '').localeCompare(b.centro?.cliente ?? '')
      || (a.centro?.codigo ?? '').localeCompare(b.centro?.codigo ?? '')
      || a.codigo.localeCompare(b.codigo));
}

type FiltroPlanes = 'vigentes' | 'borradores' | 'historicos' | 'todos';

function pasaFiltro(f: FamiliaClasificada, filtro: FiltroPlanes): boolean {
  if (filtro === 'vigentes') return f.actual !== null || f.proxima !== null;
  if (filtro === 'borradores') return f.borrador !== null;
  if (filtro === 'historicos') return f.actual === null && f.proxima === null && f.borrador === null;
  return true;
}

/**
 * F10.4 — lista compacta de planes: buscador (código, descripción, centro, cliente) y filtro (vigentes,
 * borradores, sólo histórico, todos). Una fila por familia; tareas e histórico, plegados. La comparten
 * la pestaña del centro y «Planes» del menú: una sola pantalla de planes, dos puertas.
 */
export function ListaPlanes({ planes, idCentroContexto }: {
  planes: readonly (Plan | PlanGlobal)[];
  /** En la pestaña de un centro: su id (y su cliente) — no hace falta repetirlo en cada fila. */
  idCentroContexto?: { idCentro: string; idCliente: string };
}): ReactNode {
  const lista = useListaUrl({ q: '', filtro: '' });
  const q = lista.valor('q');
  const filtro = (lista.valor('filtro') || 'todos') as FiltroPlanes;
  const familias = useMemo(() => agruparPorFamilia(planes), [planes]);
  const texto = q.trim().toLowerCase();
  const visibles = familias.filter((f) => pasaFiltro(f, filtro) && (texto === ''
    || [f.codigo, f.descripcion, f.centro?.codigo ?? '', f.centro?.descripcion ?? '', f.centro?.cliente ?? '']
      .join(' ').toLowerCase().includes(texto)));
  return (
    <>
      <div className="filtros">
        <div className="campo campo-busqueda">
          <label htmlFor="buscar-plan">Buscar plan</label>
          <input id="buscar-plan" type="search" placeholder="Código, descripción, centro o cliente…" value={q}
            onChange={(e) => lista.fijarVarios({ q: e.target.value })} style={{ minHeight: 44 }} />
        </div>
        <div className="campo">
          <label htmlFor="filtro-plan">Mostrar</label>
          <select id="filtro-plan" value={filtro} onChange={(e) => lista.fijarVarios({ filtro: e.target.value === 'todos' ? '' : e.target.value })} style={{ minHeight: 44 }}>
            <option value="todos">Todos</option>
            <option value="vigentes">Vigentes (o con próxima versión)</option>
            <option value="borradores">Con borrador</option>
            <option value="historicos">Sólo histórico</option>
          </select>
        </div>
      </div>
      <p className="suave pequena" role="status" style={{ margin: '4px 16px' }}>
        {visibles.length === familias.length ? `${String(familias.length)} planes` : `${String(visibles.length)} de ${String(familias.length)} planes`}
      </p>
      {visibles.length === 0 ? (
        <div className="panel-cuerpo"><Vacio titulo="Ningún plan coincide con la búsqueda." /></div>
      ) : (
        visibles.map((f) => (
          <BloqueFamilia key={f.clave} familia={f}
            centro={f.centro ?? (idCentroContexto === undefined ? null : { idCentro: idCentroContexto.idCentro, idCliente: idCentroContexto.idCliente })}
            mostrarCentro={f.centro !== null} etiquetaCentro={f.centro === null ? null : `${f.centro.descripcion} · ${f.centro.cliente}`} />
        ))
      )}
    </>
  );
}

/** F10.4 — «Planes» en el menú: todas las versiones del ámbito, con la misma lista que la pestaña del centro. */
export function PaginaPlanes(): ReactNode {
  const planes = useDatos(() => api.get<{ planes: PlanGlobal[] }>('/api/planes'), []);
  const { puede } = useSesion();
  const [importando, setImportando] = useState(false);
  //  Estabilización post-F13: importar un plan completo (con sus ubicaciones) y su plantilla oficial, desde aquí — sin
  //  entrada nueva en el menú (el menú son dominios; esto es una acción de «Planes»).
  const acciones = puede('importaciones:crear') ? (
    <button type="button" className={importando ? 'pequeno' : 'principal pequeno'} onClick={() => setImportando((v) => !v)}>
      {importando ? 'Cancelar' : 'Importar'}
    </button>
  ) : undefined;
  return (
    <Shell titulo="Planes de limpieza" migas={[{ texto: 'Planes' }]} acciones={acciones}>
      {importando && <ImportarPlan />}
      <div className="panel">
        <Panel estado={planes}>
          {(d) => d.planes.length === 0
            ? <div className="panel-cuerpo"><Vacio titulo="Todavía no hay planes en tus centros." /></div>
            : <div className="panel-cuerpo sin-relleno"><ListaPlanes planes={d.planes} /></div>}
        </Panel>
      </div>
    </Shell>
  );
}

/**
 * «Importar» desde Planes: una importación es de un centro (el plan y sus ubicaciones son suyos), así que se elige el
 * cliente y el centro y se sigue en «Nueva importación» de ese centro. La plantilla genérica se descarga aquí mismo.
 */
function ImportarPlan(): ReactNode {
  const navegar = useNavigate();
  const clientes = useDatos(() => api.get<{ clientes: { id: string; codigo: string; razonSocial: string; activo: boolean }[] }>('/api/clientes'), []);
  const [idCliente, setIdCliente] = useState('');
  const centros = useDatos(
    () => (idCliente === '' ? Promise.resolve({ centros: [] }) : api.get<{ centros: { id: string; codigo: string; descripcion: string }[] }>(`/api/clientes/${idCliente}/centros`)),
    [idCliente],
  );
  const [idCentro, setIdCentro] = useState('');
  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Importar un plan</h2>
      </div>
      <div className="panel-cuerpo">
        <DescargarPlantilla />
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ minWidth: 220 }}>
            <label htmlFor="imp-cliente">Cliente</label>
            <select id="imp-cliente" value={idCliente} onChange={(e) => { setIdCliente(e.target.value); setIdCentro(''); }}>
              <option value="">Elige un cliente…</option>
              {(clientes.datos?.clientes ?? []).filter((c) => c.activo).map((c) => (
                <option key={c.id} value={c.id}>{c.razonSocial} ({c.codigo})</option>
              ))}
            </select>
          </div>
          <div className="campo" style={{ minWidth: 220 }}>
            <label htmlFor="imp-centro">Centro</label>
            <select id="imp-centro" value={idCentro} disabled={idCliente === ''} onChange={(e) => setIdCentro(e.target.value)}>
              <option value="">Elige un centro…</option>
              {(centros.datos?.centros ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.descripcion} ({c.codigo})</option>
              ))}
            </select>
          </div>
          <button type="button" className="principal" disabled={idCentro === ''} onClick={() => navegar(`/centros/${idCentro}/importaciones/nueva`)}>
            Continuar
          </button>
        </div>
        {clientes.error !== null && <ErrorCaja error={clientes.error} reintentar={clientes.recargar} />}
        {centros.error !== null && <ErrorCaja error={centros.error} reintentar={centros.recargar} />}
      </div>
    </div>
  );
}

/** `fecha()` de base.tsx ya cae en "—" con NULL; esto añade el matiz de POR QUÉ. */
function vigenciaTexto(plan: Plan): string {
  if (plan.estado === 'BORRADOR' && plan.vigenteDesde === null) return 'Se determinará al publicar';
  const desde = plan.vigenteDesde === null ? '—' : formatoFecha(plan.vigenteDesde);
  const hasta = plan.vigenteHasta === null ? 'sin fin' : formatoFecha(plan.vigenteHasta);
  return `${desde} → ${hasta}`;
}

function formatoFecha(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return a === undefined || m === undefined || d === undefined ? iso : `${d}/${m}/${a}`;
}

// ------------------------------------------------------------------ página

export function PaginaPlanificacion(): ReactNode {
  const { centro } = useCentro();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [mostrarNuevo, setMostrarNuevo] = useState(false);

  const planes = useDatos(
    () => api.get<{ planes: Plan[] }>(`/api/centros/${centro.id}/planes`),
    [centro.id],
  );

  const puedeCrear = puede('planes:crear');

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Planes de limpieza</h2>
        {puedeCrear && (
          <button className={mostrarNuevo ? 'pequeno' : 'principal pequeno'} onClick={() => setMostrarNuevo((v) => !v)}>
            {mostrarNuevo ? 'Cancelar' : 'Nuevo plan'}
          </button>
        )}
      </div>
      {mostrarNuevo && (
        <div className="panel-cuerpo sep">
          <FormNuevoPlan
            idCentro={centro.id}
            alCrear={(idPlan) => navegar(`/planes/${idPlan}`)}
            alCancelar={() => setMostrarNuevo(false)}
          />
        </div>
      )}
      <Panel estado={planes}>
        {(d) =>
          d.planes.length === 0 ? (
            <div className="panel-cuerpo">
              <Vacio titulo="Este centro todavía no tiene ningún plan">
                <p className="suave">
                  Crea un plan directamente desde aquí, o impórtalo desde una plantilla XLSX. Nada se publica sin
                  revisar antes qué va a generar.
                </p>
                {puede('importaciones:crear') && (
                  <button className="pequeno" onClick={() => navegar(`../importaciones/nueva`)}>
                    Importar el primer plan
                  </button>
                )}
              </Vacio>
            </div>
          ) : (
            <div className="panel-cuerpo sin-relleno">
              <ListaPlanes planes={d.planes} idCentroContexto={{ idCentro: centro.id, idCliente: centro.idCliente }} />
            </div>
          )
        }
      </Panel>
    </div>
  );
}

// -------------------------------------------------------------- nuevo plan

function FormNuevoPlan({
  idCentro,
  alCrear,
  alCancelar,
}: {
  idCentro: string;
  alCrear: (idPlan: string) => void;
  alCancelar: () => void;
}): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<{ idPlan: string }>(`/api/centros/${idCentro}/planes`, { codigo, descripcion });
      alCrear(r.idPlan);
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => void enviar(e)}>
      <div className="form-grupo">
        <CampoTexto
          label="Código"
          value={codigo}
          onChange={setCodigo}
          required
          disabled={enviando}
          error={error instanceof FalloApi ? error.erroresDeCampo?.['codigo'] : undefined}
        />
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          required
          disabled={enviando}
          error={error instanceof FalloApi ? error.erroresDeCampo?.['descripcion'] : undefined}
        />
      </div>
      {error !== null && !(error instanceof FalloApi && error.erroresDeCampo !== undefined) && (
        <ErrorCaja error={error} />
      )}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Creando…' : 'Crear borrador'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ bloque familia

function BloqueFamilia({ familia, centro, mostrarCentro, etiquetaCentro }: {
  familia: FamiliaConCentro;
  centro: { idCentro: string; idCliente: string } | null;
  mostrarCentro: boolean;
  etiquetaCentro: string | null;
}): ReactNode {
  const { puede } = useSesion();
  const navegarBase = useNavigate();
  const ubicacion = useLocation();
  //  F10.4: abrir un plan desde aquí y volver trae de vuelta a ESTA lista, con su búsqueda y filtro.
  const volver = { a: `${ubicacion.pathname}${ubicacion.search}`, texto: mostrarCentro ? 'Planes' : 'Planificación del centro' };
  const navegar = (a: string): void => { void navegarBase(a, { state: { volver } }); };
  //  F10.4: plegado por defecto — una pantalla por plan era lo que hacía la lista interminable.
  const [verTareasDe, setVerTareasDe] = useState<Plan | null>(null);
  const [verHistorico, setVerHistorico] = useState(false);
  const planDelPlanner = familia.actual ?? familia.proxima;

  const puedeCrear = puede('planes:crear');
  const puedeEditar = puede('planes:editar');

  //  Checkpoint post-F14.3: igual que la ficha del plan (editor-plan.tsx) — un clic, una petición (el botón se
  //  desactiva mientras tanto) y el error a la vista, nunca una promesa rechazada en silencio.
  const [creandoVersion, setCreandoVersion] = useState(false);
  const [errorVersion, setErrorVersion] = useState<unknown>(null);
  async function crearNuevaVersion(): Promise<void> {
    if (familia.actual === null || creandoVersion) return;
    setCreandoVersion(true);
    setErrorVersion(null);
    try {
      const r = await api.post<{ idPlanBorrador: string }>(`/api/planes/${familia.actual.id}/nueva-version`);
      navegar(`/planes/${r.idPlanBorrador}`);
    } catch (e) {
      setErrorVersion(e);
    } finally {
      setCreandoVersion(false);
    }
  }

  return (
    <div className="sep">
      <div className="fila" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 className="mono" style={{ margin: 0 }}>{familia.codigo}</h3>
          <div className="pequena suave">{familia.descripcion}{mostrarCentro && etiquetaCentro !== null && <> · {etiquetaCentro}</>}</div>
        </div>
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          {centro !== null && planDelPlanner !== null && puede('ots:leer') && (
            <button className="pequeno" style={{ minHeight: 44 }}
              onClick={() => navegar(`/planner?idCliente=${centro.idCliente}&idCentro=${centro.idCentro}&idPlan=${planDelPlanner.id}&vista=semana`)}>
              Ver en el Planner
            </button>
          )}
          {familia.borrador !== null ? (
            <button className="principal pequeno" style={{ minHeight: 44 }} onClick={() => navegar(`/planes/${familia.borrador!.id}`)}>
              Continuar borrador
            </button>
          ) : (
            puedeCrear &&
            familia.actual !== null && (
              <button className="pequeno" style={{ minHeight: 44 }} disabled={creandoVersion} onClick={() => void crearNuevaVersion()}>
                {creandoVersion ? 'Creando…' : 'Crear nueva versión'}
              </button>
            )
          )}
        </div>
      </div>
      {errorVersion !== null && <ErrorCaja error={errorVersion} />}

      <div className="tarjetas-plan-estado">
        {familia.actual !== null && (
          <TarjetaEstadoPlan
            titulo="PLAN ACTUAL"
            clase="exito"
            plan={familia.actual}
            detalle={`aplicable ${familia.actual.vigenteHasta === null ? 'sin fecha de fin' : `hasta ${formatoFecha(familia.actual.vigenteHasta)}`}`}
            enFoco={verTareasDe?.id === familia.actual.id}
            onVerTareas={() => setVerTareasDe((v) => (v?.id === familia.actual!.id ? null : familia.actual))}
            onAbrir={() => navegar(`/planes/${familia.actual!.id}`)}
          />
        )}
        {familia.proxima !== null && (
          <TarjetaEstadoPlan
            titulo="PRÓXIMA VERSIÓN"
            clase="info"
            plan={familia.proxima}
            detalle={`entra en vigor ${familia.proxima.vigenteDesde === null ? '—' : formatoFecha(familia.proxima.vigenteDesde)}`}
            enFoco={verTareasDe?.id === familia.proxima.id}
            onVerTareas={() => setVerTareasDe((v) => (v?.id === familia.proxima!.id ? null : familia.proxima))}
            onAbrir={() => navegar(`/planes/${familia.proxima!.id}`)}
          />
        )}
        {familia.borrador !== null && (
          <TarjetaEstadoPlan
            titulo="BORRADOR"
            clase="aviso"
            plan={familia.borrador}
            detalle="en edición"
            enFoco={verTareasDe?.id === familia.borrador.id}
            onVerTareas={() => setVerTareasDe((v) => (v?.id === familia.borrador!.id ? null : familia.borrador))}
            onAbrir={() => navegar(`/planes/${familia.borrador!.id}`)}
          />
        )}
        {familia.actual === null && familia.proxima === null && familia.borrador === null && (
          <p className="suave pequena">Esta familia sólo tiene versiones históricas.</p>
        )}
      </div>

      {verTareasDe !== null && <TablaTareas key={verTareasDe.id} idPlan={verTareasDe.id} />}

      {familia.historico.length > 0 && (
        <button type="button" className="enlace pequena" style={{ minHeight: 44 }} aria-expanded={verHistorico} onClick={() => setVerHistorico((v) => !v)}>
          {verHistorico ? 'Ocultar versiones anteriores' : `Versiones anteriores (${String(familia.historico.length)})`}
        </button>
      )}
      {verHistorico && <Historico planes={familia.historico} onAbrir={(id) => navegar(`/planes/${id}`)} />}

      {!puedeEditar && familia.borrador === null && familia.actual === null && familia.proxima === null && (
        <p className="suave pequena">Sin versión aplicable ni próxima: sólo histórico.</p>
      )}
    </div>
  );
}

function TarjetaEstadoPlan({
  titulo,
  clase,
  plan,
  detalle,
  enFoco,
  onVerTareas,
  onAbrir,
}: {
  titulo: string;
  clase: 'exito' | 'info' | 'aviso';
  plan: Plan;
  detalle: string;
  enFoco: boolean;
  onVerTareas: () => void;
  onAbrir: () => void;
}): ReactNode {
  return (
    <div className={`panel tarjeta-plan-estado ${enFoco ? 'en-foco' : ''}`}>
      <div className="panel-cuerpo">
        <span className={`etiqueta ${clase}`}>{titulo}</span>
        <div className="fila" style={{ gap: 8, marginTop: 6 }}>
          <strong>v{plan.version}</strong>
          <span className="suave pequena">{detalle}</span>
        </div>
        <div className="pequena tenue" style={{ marginTop: 2 }}>{vigenciaTexto(plan)}</div>
        <div className="pequena tenue">{plan.lineas} tarea{plan.lineas === 1 ? '' : 's'}</div>
        <div className="fila" style={{ gap: 8, marginTop: 8 }}>
          <button type="button" className="pequeno" style={{ minHeight: 44 }} onClick={onVerTareas} aria-expanded={enFoco}>
            {enFoco ? 'Ocultar tareas' : 'Ver tareas'}
          </button>
          <button type="button" className="pequeno" style={{ minHeight: 44 }} onClick={onAbrir}>
            {plan.estado === 'BORRADOR' ? 'Editar' : 'Abrir'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- tabla tareas

function TablaTareas({ idPlan }: { idPlan: string }): ReactNode {
  const estado = useDatos(
    () => api.get<{ lineas: LineaPlan[] }>(`/api/planes/${idPlan}/lineas`),
    [idPlan],
  );
  const [busqueda, setBusqueda] = useState('');
  const [soloFuera, setSoloFuera] = useState(false);

  const filtradas = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return (estado.datos?.lineas ?? []).filter((l) => {
      if (soloFuera && !l.fueraContrato) return false;
      if (t === '') return true;
      return [l.referenciaTarea, l.ubicacion, l.descripcionTarea ?? '', l.tipo]
        .join(' ')
        .toLowerCase()
        .includes(t);
    });
  }, [estado.datos, busqueda, soloFuera]);

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Tareas del plan</h2>
        <span className="suave pequena">
          {filtradas.length} de {estado.datos?.lineas.length ?? 0}
        </span>
      </div>
      <div className="filtros">
        <div className="campo campo-busqueda">
          <label htmlFor="buscar-tarea">Buscar</label>
          <input
            id="buscar-tarea"
            type="text"
            placeholder="Referencia, ubicación, descripción…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="fuera">Clasificación</label>
          <select
            id="fuera"
            value={soloFuera ? 'fuera' : 'todas'}
            onChange={(e) => setSoloFuera(e.target.value === 'fuera')}
          >
            <option value="todas">Todas</option>
            <option value="fuera">Sólo fuera de contrato</option>
          </select>
        </div>
      </div>
      <div className="panel-cuerpo sin-relleno">
        <Panel estado={estado}>
          {() =>
            filtradas.length === 0 ? (
              <Vacio titulo="Ninguna tarea coincide" />
            ) : (
              <div className="tabla-desliza">
                <table>
                  <thead>
                    <tr>
                      <th>Referencia</th>
                      <th>Ubicación</th>
                      <th>Descripción</th>
                      <th>Tipo</th>
                      <th>Frecuencia</th>
                      <th>Festivos</th>
                      <th>Contrato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtradas.map((l) => (
                      <tr key={l.id}>
                        <td className="cod">{l.referenciaTarea}</td>
                        <td>
                          <div className="cod">{l.ubicacion}</div>
                          {l.ubicacionRuta !== null && (
                            <div className="pequena tenue">{l.ubicacionRuta}</div>
                          )}
                        </td>
                        <td>{l.descripcionTarea ?? '—'}</td>
                        <td className="cod">{l.tipo}</td>
                        <td>{l.frecuencia}</td>
                        <td className="suave pequena">{l.politicaFestivo}</td>
                        <td>
                          {l.fueraContrato ? (
                            <span className="etiqueta aviso">FUERA</span>
                          ) : (
                            <span className="suave pequena">Incluida</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Panel>
      </div>
    </div>
  );
}

function Historico({ planes, onAbrir }: { planes: readonly Plan[]; onAbrir: (id: string) => void }): ReactNode {
  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Histórico de versiones</h2>
      </div>
      <div className="panel-cuerpo sin-relleno">
        <div className="tabla-desliza">
          <table>
            <thead>
              <tr>
                <th className="num">Versión</th>
                <th>Estado</th>
                <th>Vigencia</th>
                <th className="num">Tareas</th>
                <th>Fuente</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {planes.map((p) => (
                <tr key={p.id} className="pulsable" onClick={() => onAbrir(p.id)}>
                  <td className="num">v{p.version}</td>
                  <td>
                    <EstadoPlan estado={p.estado} />
                  </td>
                  <td>{vigenciaTexto(p)}</td>
                  <td className="num">{p.lineas}</td>
                  <td className="suave pequena">{p.fuente ?? '—'}</td>
                  <td className="derecha">
                    <button className="pequeno" onClick={(e) => { e.stopPropagation(); onAbrir(p.id); }}>Abrir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
